import { FC, useEffect, useState } from 'react';
import {
  TextField,
  Box,
  styled,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';

import { observer } from 'mobx-react-lite';
import AddIcon from '@mui/icons-material/Add';
import { TelefonBaseEntry } from '@codefluegel/wowis-rest-typescript-client';
import { autorun } from 'mobx';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import useMedia from '../../hooks/useMedia';
import { useKundenApi } from '../../ApiClient';
import ContactModal from '../dialog/ContactModal';
import EditPencil from '../../assets/MeineDaten/Edit_Pencil.svg';
import PasswordModal from '../dialog/PasswordModal';

const StyledTextField = styled(TextField)({
  '& .MuiInputLabel-root': { fontSize: '0.9rem' },
});

export interface GetTextListState {
  getTextListState: () => string[];
}

interface Props {
  topUserId: number;
  variant?: 'standard' | 'outlined' | 'filled';
}

const ContactListForm: FC<Props> = observer(props => {
  const { topUserId, variant } = props;
  const [textFields, setTextFields] = useState<TelefonBaseEntry[]>([]);
  const isMobile = useMedia('md');
  const kundenApi = useKundenApi();
  const [contactModalOpened, setContactModalOpened] = useState(false);
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<
    TelefonBaseEntry | undefined
  >(undefined);
  const [newLfdNr, setNewLfdNr] = useState<number | undefined>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showPassowrodDialog, setShowPassowrodDialog] = useState(false);

  useEffect(
    () =>
      autorun(() => {
        async function getContactData() {
          const { data } = await kundenApi.getAllKundenTelefonDaten(topUserId);
          setTextFields(data);
        }
        getContactData();
      }),
    [kundenApi],
  );

  const updateTextField = (contact: TelefonBaseEntry) => {
    if (contactToEdit) {
      const nextTextFields = textFields.map(c => {
        if (c.telId === contact.telId) {
          return contact;
        }
        return c;
      });
      setTextFields(nextTextFields);
    } else {
      setTextFields([...textFields, contact]);
    }
    setContactToEdit(undefined);
  };

  const handleChangeEmailTextField = (index: number, value: string) => {
    const updatedFields = [...textFields];
    updatedFields[index].email = value; // Update the text field value at the specified index
    setTextFields(updatedFields);
  };

  const handleChangeTelTextField = (index: number, value: string) => {
    const updatedFields = [...textFields];
    updatedFields[index].telnr = value; // Update the text field value at the specified index
    setTextFields(updatedFields);
  };

  const handleEditContact = (contact?: TelefonBaseEntry) => {
    if (contact) {
      setContactToEdit(contact);
      setNewLfdNr(undefined);
    } else {
      setContactToEdit(undefined);
      if (textFields.length > 0) {
        const lastEntryLfdNr = textFields
          .sort((a, b) => (a.lfd_nr ?? -1) - (b.lfd_nr ?? -1))
          .slice(-1)[0].lfd_nr;
        if (lastEntryLfdNr) {
          const lfdNr = lastEntryLfdNr + 1;
          setNewLfdNr(lfdNr);
        }
      } else {
        setNewLfdNr(1);
      }
    }
    setContactModalOpened(true);
  };

  const handleDeleteContact = async () => {
    if (contactToEdit) {
      try {
        const newContact: TelefonBaseEntry = {
          telId: contactToEdit?.telId,
          lfd_nr: contactToEdit?.lfd_nr,
          email: '',
          telnr: '',
        };
        await kundenApi.updateKundenTelefonDaten(topUserId, newContact);
        const indexToDelete = textFields.indexOf(contactToEdit);
        if (indexToDelete !== -1) {
          const updatedFields = [...textFields];
          updatedFields.splice(indexToDelete, 1);
          setTextFields(updatedFields);
        }
      } catch (error) {
        if (error instanceof Error && typeof error.message === 'string') {
          setSnackbarMessage(error.message);
        }
        setSnackbarOpen(true);
      }
      setDeleteDialogOpened(false);
      setContactToEdit(undefined);
    }
  };

  const handlePasswordPassed = () => {
    if (contactModalOpened) {
      handleEditContact();
    } else if (deleteDialogOpened) {
      handleDeleteContact();
    }
  };

  return (
    <Box width={1} overflow={isMobile ? 'none' : 'auto'} paddingX={4}>
      {textFields.map((contact, index) => (
        <Box key={contact.telId} paddingBottom={4} my={1}>
          <Box display="flex" flexDirection="row" alignItems="center">
            <Typography paddingRight={2} color="secondary">
              Kontakt {(index + 1).toString()}
            </Typography>
            <IconButton size="small" onClick={() => handleEditContact(contact)}>
              <img src={EditPencil} alt="Icon" width={20} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setDeleteDialogOpened(true);
                setContactToEdit(contact);
              }}
            >
              <DeleteOutlineOutlinedIcon color="primary" />
            </IconButton>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            paddingTop={2}
          >
            <Box flexDirection="column" alignContent="center">
              <StyledTextField
                sx={{ paddingBottom: 2 }}
                variant={variant || 'standard'}
                fullWidth
                value={contact.email}
                onChange={e =>
                  handleChangeEmailTextField(index, e.target.value)
                }
                label={`${'Email'} ${index + 1}`}
                disabled
              />
              <StyledTextField
                variant={variant || 'standard'}
                fullWidth
                value={contact.telnr}
                onChange={e => handleChangeTelTextField(index, e.target.value)}
                label={`${'Telefonnummer'} ${index + 1}`}
                disabled
              />
            </Box>
          </Box>
        </Box>
      ))}
      <Box
        justifyContent="center"
        paddingBottom={2}
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <IconButton size="small" onClick={() => handleEditContact()}>
          <AddIcon />
        </IconButton>
      </Box>

      <ContactModal
        contactToEdit={contactToEdit}
        topUserId={topUserId}
        lfdNr={newLfdNr}
        open={contactModalOpened}
        onClose={() => setContactModalOpened(false)}
        updateTextField={updateTextField}
      />

      <Dialog
        open={deleteDialogOpened}
        onClose={() => setDeleteDialogOpened(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Sind Sie sicher, dass Sie den Kontakt löschen möchten?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpened(false)}>Nein</Button>
          <Button
            // onClick={() => setShowPassowrodDialog(true)}
            onClick={() => handleDeleteContact()}
            autoFocus
          >
            Ja
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <PasswordModal
        open={showPassowrodDialog}
        onClose={() => setShowPassowrodDialog(false)}
        onPasswordPassed={handlePasswordPassed}
      />
    </Box>
  );
});

export default ContactListForm;
